import { useState, useRef, useEffect, useContext } from 'react'
import { PaperAirplaneIcon, StarIcon, XMarkIcon } from '@heroicons/react/20/solid'
import LoadingComponent from './loading'
import { AuthContext } from "../AuthContext";
import SarsRequest from './sarsrequest'

import { motion, AnimatePresence } from 'framer-motion'

export default function SharkAIChat({ AiAssistant, onClose, isMobile }) {
  const user = useContext(AuthContext);
  const [formData, setFormData] = useState({ comment: '' })
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(false)
  const [helpfulClicked, setHelpfulClicked] = useState({})
  const [ratings, setRatings] = useState({})
  const [sarsMessage, setSarsMessage] = useState('')
  const [activeView, setActiveView] = useState('chat')
  const chatEndRef = useRef(null)

  const canRequestSars = ['admin'].includes(String(user?.access || '').toLowerCase())

  // Audio refs
  const popAudio = useRef(null)
  const wooshAudio = useRef(null)

  // Fetch old messages and poll every 1 minute
  useEffect(() => {
    let intervalId

    const fetchOldMessages = async () => {
      try {
        const response = await fetch('https://api.insightos.co.uk/api/helpassistant/getoldMessages', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        })
        const data = await response.json()
        // Each message contains user, ai, and possibly human
        // Flatten into multiple messages per entry, and track id
        // Sort by creation time to show oldest first (latest at bottom)
        const sortedData = data.data.sort((a, b) => new Date(a.CreatedAt || 0) - new Date(b.CreatedAt || 0))
        const formattedMessages = sortedData.flatMap((message) => {
          const arr = []
          if (message.Question) arr.push({ type: 'user', text: message.Question })
          if (message.AIAnswer) arr.push({ type: 'ai', text: message.AIAnswer, id: message.id, rating: message.Rating, escalate: message.Escalate })
          if (message.Human) {
            let sarsContent = null
            if (message.SARSContent) {
              try {
                const parsedSarsContent = typeof message.SARSContent === 'string'
                  ? JSON.parse(message.SARSContent)
                  : message.SARSContent
                sarsContent = Array.isArray(parsedSarsContent) ? parsedSarsContent : null
              } catch {
                sarsContent = null
              }
            }

            arr.push({ type: 'human', text: message.Human, sarsContent, ticketId: message.id })
          }
          return arr
        })
        setMessages(formattedMessages)
      } catch (error) {
        console.error('Error fetching old messages:', error)
        setMessages([])
      }
    }

    fetchOldMessages()
    intervalId = setInterval(fetchOldMessages, 60000) // poll every 1 min

    return () => clearInterval(intervalId)
  }, [])

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading])

  const handleInputChange = (event) => {
    setFormData({ ...formData, comment: event.target.value })
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!formData.comment.trim()) return
    setMessages((prev) => [...prev, { type: 'user', text: formData.comment }])
    setLoading(true)
    setFormData({ comment: '' })
    wooshAudio.current && wooshAudio.current.play().catch(() => {})
    try {
      const response = await fetch('https://api.insightos.co.uk/api/helpassistant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userQuestion: formData.comment }),
      })
      const data = await response.json()
      setMessages((prev) => [
        ...prev,
        { type: 'ai', text: data.data ? data.data : 'No response from assistant.', id: data.id },
      ])
      popAudio.current && popAudio.current.play().catch(() => {})
    } catch {
      setMessages((prev) => [...prev, { type: 'ai', text: 'Error contacting assistant.' }])
      popAudio.current && popAudio.current.play().catch(() => {})
    } finally {
      setLoading(false)
    }
  }

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text).catch(() => {})
  }

  const handleStarClick = (msgId, star) => {
    setRatings((prev) => ({ ...prev, [msgId]: star }))
    setHelpfulClicked((prev) => ({ ...prev, [msgId]: true }))
  }

  const handleEscalate = async (msgId, rating) => {
    setMessages((prev) => [
      ...prev,
      { type: 'ai', text: 'Escalating your request to a human agent, you will be sent an email with a response. Thanks for your patience' },
    ])
    popAudio.current && popAudio.current.play().catch(() => {})
    setHelpfulClicked((prev) => ({ ...prev, [msgId]: false }))
    setRatings((prev) => ({ ...prev, [msgId]: 0 }))
console.log(msgId)
    try {
      const response = await fetch('https://api.insightos.co.uk/api/escalate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          rating: rating,
          id: msgId,
        }),
      })
      const data = await response.json()
      setMessages((prev) => [
        ...prev,
        { type: 'ai', text: data.data ? data.data : 'No response from assistant.' },
      ])
    } catch {
      setMessages((prev) => [...prev, { type: 'ai', text: 'Error escalating request.' }])
    }
  }

  const csvEscape = (value) => {
    const normalized = value === null || value === undefined ? '' : String(value)
    return `"${normalized.replace(/"/g, '""')}"`
  }

  const handleDownloadSarsCsv = (rows = [], ticketId) => {
    if (!Array.isArray(rows) || rows.length === 0) return

    const columns = ['content', 'from', 'to', 'createdAt']
    const csvLines = [columns.join(',')]

    rows.forEach((row) => {
      csvLines.push([
        csvEscape(row?.content),
        csvEscape(row?.from),
        csvEscape(row?.to),
        csvEscape(row?.createdAt),
      ].join(','))
    })

    const csvBlob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' })
    const downloadUrl = URL.createObjectURL(csvBlob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.setAttribute('download', `sars-content-${ticketId || 'export'}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(downloadUrl)
  }

  // This will call the parent's onClose handler
  const handleClose = () => {
    if (onClose) onClose()
  }

  // Message bubble color by type with glassmorphism effect
  const getBubbleClass = (type) => {
    if (type === 'user') return 'bg-gradient-to-r from-blue-100/70 to-indigo-100/70 backdrop-blur-md border border-blue-200/60 text-right text-slate-800 shadow-lg'
    if (type === 'ai') return 'bg-gradient-to-r from-white/80 to-purple-100/70 backdrop-blur-md border border-purple-200/60 text-left text-slate-800 shadow-lg'
    if (type === 'human') return 'bg-gradient-to-r from-purple-100/70 to-blue-100/70 backdrop-blur-md border border-indigo-200/60 text-left text-slate-800 shadow-lg'
    return ''
  }

  return (
    <>
      {/* Audio elements */}
      <audio ref={popAudio} src="/pop.mp3" preload="auto" />
      <audio ref={wooshAudio} src="/woosh.mp3" preload="auto" />
      <AnimatePresence>
        <motion.div
          key="shark-ai-chat"
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 40, scale: 0.95 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className={`
            fixed z-50 
            ${isMobile 
              ? 'inset-0 w-full h-full' 
              : 'w-full max-w-md md:max-w-sm lg:max-w-md bottom-0 md:bottom-8 md:right-8 left-1/2 md:left-auto transform md:translate-x-0 -translate-x-1/2 md:rounded-xl'}
            shadow-2xl backdrop-blur-xl bg-gradient-to-br from-blue-500/25 via-white/35 to-purple-500/25 border border-white/40 flex flex-col
            before:absolute before:inset-0 before:bg-gradient-to-br before:from-white/40 before:via-transparent before:to-white/10 before:rounded-xl before:pointer-events-none
          `}
          style={{ minHeight: isMobile ? '100%' : 400, maxHeight: isMobile ? '100%' : 600 }}
        >
          {/* Futuristic header with glassmorphism */}
          <div className="flex items-start justify-between px-6 pt-4 pb-3 border-b border-white/30 bg-gradient-to-r from-blue-200/40 via-white/30 to-purple-200/40 backdrop-blur-md">
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full animate-pulse shadow-lg shadow-blue-400/40"></div>
              <div>
                <div className="font-bold text-lg bg-gradient-to-r from-blue-700 to-purple-700 bg-clip-text text-transparent">
                  Support Center
                </div>
                {canRequestSars && (
                  <button
                    type="button"
                    onClick={() => {
                      setActiveView('sars')
                      setSarsMessage('')
                    }}
                    className="mt-2 rounded-lg px-3 py-1.5 text-xs font-semibold text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 transition-all"
                  >
                    SARS Request
                  </button>
                )}
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 rounded-full hover:bg-white/40 bg-white/20 transition-all duration-300 backdrop-blur-md border border-white/40 group"
              aria-label="Close chat"
              type="button"
            >
              <XMarkIcon className="h-5 w-5 text-slate-700 group-hover:text-red-500 transition-colors" />
            </button>
          </div>

          {activeView === 'sars' ? (
            <SarsRequest onBack={() => setActiveView('chat')} />
          ) : (
            <>
          {/* Messages container with custom scrollbar */}
          <div className="flex flex-col flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin scrollbar-thumb-white/30 scrollbar-track-transparent">
            {messages.map((msg, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1, duration: 0.3 }}
                className={`flex flex-col ${
                  msg.type === 'user'
                    ? 'items-end'
                    : 'items-start'
                }`}
              >
                <div
                  className={`
                    px-4 py-3 rounded-2xl max-w-[80%] text-sm relative overflow-hidden
                    ${getBubbleClass(msg.type)}
                    transform transition-all duration-300 hover:scale-105 hover:shadow-xl
                  `}
                >
                  <div className="relative z-10">
                    {msg.text}
                    {(msg.type === 'ai' || msg.type === 'human') && (
                      <button
                        onClick={() => handleCopyToClipboard(msg.text)}
                        className="ml-2 text-xs text-blue-600 hover:text-purple-700 underline transition-colors"
                      >
                        Copy
                      </button>
                    )}
                  </div>
                </div>
                
                {msg.type === 'ai' && msg.escalate === 1 && (
                  <motion.span 
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-red-700 text-sm mt-2 p-3 rounded-lg bg-red-100/70 backdrop-blur-md border border-red-300/40 animate-pulse ml-2 shadow-lg"
                  >
                    🚀 Escalated To Support, Awaiting Response...
                  </motion.span>
                )}

                {msg.type === 'human' && Array.isArray(msg.sarsContent) && msg.sarsContent.length > 0 && (
                  <button
                    type="button"
                    onClick={() => handleDownloadSarsCsv(msg.sarsContent, msg.ticketId)}
                    className="mt-2 ml-2 text-xs px-3 py-2 rounded-full bg-blue-100/80 text-blue-700 border border-blue-300/50 hover:bg-blue-200/80 transition-all duration-300 backdrop-blur-sm"
                  >
                    Download
                  </button>
                )}

                {/* Futuristic feedback actions */}
                {msg.type === 'ai' && msg.escalate === 0 && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="flex items-center gap-3 mt-3 p-2 rounded-lg bg-white/45 backdrop-blur-md border border-white/50"
                  >
                    <button
                      className="text-xs px-3 py-2 rounded-full bg-green-100/80 text-green-700 border border-green-300/50 hover:bg-green-200/80 transition-all duration-300 backdrop-blur-sm"
                      disabled={helpfulClicked[msg.id]}
                    >
                      ✨ Helpful
                    </button>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => handleStarClick(msg.id, star)}
                          className="p-1 rounded-full hover:bg-white/20 transition-all duration-200"
                          disabled={helpfulClicked[msg.id]}
                        >
                          <StarIcon
                            className={`h-4 w-4 transition-all duration-200 ${
                              star <= (ratings[msg.id] || 0) 
                                ? 'text-yellow-500 drop-shadow-lg' 
                                : 'text-gray-500 hover:text-yellow-400'
                            }`}
                            aria-hidden="true"
                          />
                        </button>
                      ))}
                    </div>
                    <button
                      className="text-xs px-3 py-2 rounded-full bg-red-100/80 text-red-700 border border-red-300/50 hover:bg-red-200/80 transition-all duration-300 backdrop-blur-sm"
                      onClick={() => handleEscalate(msg.id, ratings[msg.id] || 0)}
                    >
                      🚀 Escalate
                    </button>
                    {helpfulClicked[msg.id] && (
                      <motion.span 
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="ml-2 text-green-700 text-xs flex items-center"
                      >
                        ✅ Thank you!
                      </motion.span>
                    )}
                  </motion.div>
                )}
              </motion.div>
            ))}
            {loading && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex justify-start"
              >
                <div className="px-4 py-3 rounded-2xl bg-gradient-to-r from-blue-100/80 to-purple-100/80 backdrop-blur-md border border-purple-200/50 text-slate-800 text-sm shadow-lg">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce delay-100"></div>
                    <div className="w-2 h-2 bg-indigo-500 rounded-full animate-bounce delay-200"></div>
                    <span className="ml-2">AI is thinking...</span>
                  </div>
                </div>
              </motion.div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Futuristic input section */}
          <form onSubmit={handleSubmit} className="flex items-center gap-3 p-4 border-t border-white/30 bg-gradient-to-r from-blue-100/40 via-white/30 to-purple-100/40 backdrop-blur-md">
            <div className="flex-1 relative">
              <textarea
                onChange={handleInputChange}
                id="comment"
                name="comment"
                rows={1}
                className="w-full resize-none rounded-xl bg-white/80 backdrop-blur-md border border-white/60 px-4 py-3 text-slate-800 placeholder:text-slate-500 focus:ring-2 focus:ring-purple-400 focus:border-transparent outline-none transition-all duration-300"
                value={formData.comment}
                placeholder="Type your question..."
                disabled={loading}
              />
              <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 pointer-events-none"></div>
            </div>
            <button
              type="submit"
              className="rounded-full p-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-500 hover:to-purple-500 transition-all duration-300 transform hover:scale-110 shadow-lg shadow-purple-500/25 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              disabled={loading || !formData.comment.trim()}
            >
              <PaperAirplaneIcon aria-hidden="true" className="h-5 w-5" />
            </button>
          </form>
          </>
          )}
        </motion.div>
      </AnimatePresence>
    </>
  )
}
